import {
    Box,
    Grid,
    Menu,
    MenuItem,
    Stack,
    Button,
    Tabs,
    Tab,
    Checkbox,
    CircularProgress,
    Typography
} from '@mui/material';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/DataTable/DataTable';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import SectionHeader from '../../components/SectionHeader';
import useGlobalFilters from '../../hooks/useGlobalFilters';
import VisitasFilters from './components/VisitasFilters';
import useVisitas from './hooks/useVisitas';
import NoTableDataImageBO from '../../assets/image/empty-state-establecimientos-backoffice.png';
import DrawerVisitas from './components/DrawerVisitas';
import { useRoles } from '../../hooks/useRoles';
import { useRoutePrefix } from '../../hooks/useRoutePrefix';
import { VisitaEstadoChip } from './components/EstadoChip';
import { getAccessValues } from '../../components/layout/RoutesManager/access';
import useUserAccess from '../../hooks/useUserAccess';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { VisitaApi } from './consts/types';
import { getDate, getYear } from '../../utils/common';
import DrawerManager, { DRAWER_ACTION_VALUES } from './components/drawers/DrawerManager';
import { esEstadoDescargaActaPermitido, VisitaEstado } from './consts/visitasStatus';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from './consts/visitTypeDisplayNames';
import { VISIT_TABS_INDEX } from '../../redux/slices/visitsSlice';
import useVisitActions from './hooks/useVisitActions';
import { useDescargaPdfInvestigacionVisita } from './hooks/useDescargaPdfInvestigacionVisita';
import { esVisitaDeInvestigacion, puedeDescargarPdfInvestigacion } from './consts/pdfInvestigacion';
import { useDescargaInformacionAdicional } from './hooks/useDescargaInformacionAdicional';
import { puedeDescargarInformacionAdicional } from './consts/informacionAdicional';
import HomeAgenda from './components/HomeAgenda';
import DescargarActasFallidaDrawer from './components/drawers/DescargarActasFallidaDrawer';
import useActaFallida from './hooks/useActaFallida';
import { puedeDescargarActaFallida } from './consts/actaFallida';
import { useDispatch } from 'react-redux';
import { setCurrentTab } from '../../redux/slices/visitsSlice';
import dayjs from 'dayjs';
import { Tooltip } from '@mui/material';
import { VisitasIndicators } from './components/VisitIndicators';
import LugarOcurrenciaSeleccionado, {
    getOccurrenceLocationDisplayData
} from './components/LugarOcurrenciaSeleccionado';
import {
    canPreventorRegisterVisit,
    canSeeVisitDetail,
    resolveVisitDetailsAction
} from './consts/visitEditability';
import { clearAllExecutionVisitDrafts } from './EjecutionsVisits/hooks/useEjecutionVisits';
import {
    esDeEstablecimientoDadoDeBaja,
    puedeVerEjecucionVisita,
    resolveVisitTabIndexByEstado
} from './consts/accionesVisita';

const SOON_DAYS_THRESHOLD = 7;

const ASSIGNATION_VALID_STATES = [VisitaEstado.SIN_ASIGNAR];

const REASSIGNATION_VALID_STATES = [
    VisitaEstado.RECHAZADO,
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA,
    VisitaEstado.EN_PROCESO
];

const ACTA_DOWNLOAD_ALLOWED_STATES = [VisitaEstado.PRESENTADO, VisitaEstado.REALIZADA];

const CANCELABLE_VISIT_TABS = [
    VISIT_TABS_INDEX.REJECTED,
    VISIT_TABS_INDEX.PENDING,
    VISIT_TABS_INDEX.PROGRAMMED,
    VISIT_TABS_INDEX.IN_PROGRESS
];

const CANCELABLE_VISIT_STATES = [
    VisitaEstado.RECHAZADO,
    VisitaEstado.SIN_ASIGNAR,
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA,
    VisitaEstado.EN_PROCESO
];

const REJECTABLE_VISIT_STATES = [
    VisitaEstado.PENDIENTE,
    VisitaEstado.PROGRAMADA
];

const columnTitles = (
    t: (key: string) => string,
    isPreventor: boolean | undefined,
    isAdminBO: boolean | undefined,
    currentTabIndex?: number
): string[] => {
    const titles = [
        t('table.columnsTitles.id'),
        t('table.columnsTitles.establishment'),
        t('table.columnsTitles.contract'),
        t('table.columnsTitles.preventor'),
        t('table.columnsTitles.consultora'),
        t('table.columnsTitles.city'),
        t('table.columnsTitles.province'),
        t('table.columnsTitles.state'),
        t('table.columnsTitles.visitType'),
        t('table.columnsTitles.visitProgrammedDate'),
        t('table.columnsTitles.visitLimitDate'),
        t('table.columnsTitles.visitMadeDate'),
        t('table.columnsTitles.anio'),
        t('table.columnsTitles.actions')
    ];

    let filteredTitles = titles;

    if (isPreventor) {
        filteredTitles = filteredTitles.filter(
            (title) =>
                title !== t('table.columnsTitles.preventor') &&
                title !== t('table.columnsTitles.consultora')
        );
    }

    const shouldShowProgrammedDate =
        (isAdminBO && currentTabIndex === VISIT_TABS_INDEX.PROGRAMMED) || isPreventor;

    if (!shouldShowProgrammedDate) {
        filteredTitles = filteredTitles.filter(
            (title) => title !== t('table.columnsTitles.visitProgrammedDate')
        );
    }

    if (
        currentTabIndex === VISIT_TABS_INDEX.CLOSED ||
        currentTabIndex === VISIT_TABS_INDEX.IN_REVISION
    ) {
        filteredTitles = filteredTitles.filter(
            (title) => title !== t('table.columnsTitles.visitLimitDate')
        );
    }

    if (
        currentTabIndex !== VISIT_TABS_INDEX.ALL &&
        currentTabIndex !== VISIT_TABS_INDEX.IN_REVISION &&
        currentTabIndex !== VISIT_TABS_INDEX.CLOSED
    ) {
        filteredTitles = filteredTitles.filter(
            (title) => title !== t('table.columnsTitles.visitMadeDate')
        );
    }

    return filteredTitles;
};

const renderOccurrenceLocationTooltip = (item: VisitaApi, content: ReactNode = '-') => {
    const lugarOcurrencia = item?.siniestro?.lugarOcurrencia;
    const tooltipData = getOccurrenceLocationDisplayData(
        {
            calle: lugarOcurrencia?.calle,
            altura: lugarOcurrencia?.altura,
            piso: lugarOcurrencia?.piso,
            depto: lugarOcurrencia?.depto,
            codigoPostal: lugarOcurrencia?.codigoPostal,
            localidadData: lugarOcurrencia?.localidad
        },
        item?.siniestro?.id
    );

    if (!tooltipData) {
        return content;
    }

    return (
        <Tooltip
            enterDelay={200}
            arrow
            title={
                <LugarOcurrenciaSeleccionado
                    occurrenceLocation={{
                        calle: lugarOcurrencia?.calle,
                        altura: lugarOcurrencia?.altura,
                        piso: lugarOcurrencia?.piso,
                        depto: lugarOcurrencia?.depto,
                        codigoPostal: lugarOcurrencia?.codigoPostal,
                        localidadData: lugarOcurrencia?.localidad
                    }}
                    claimNumber={item?.siniestro?.nroRoam || item?.siniestro?.id}
                    variant="tooltip"
                />
            }
        >
            <Box
                component="span"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: 32
                }}
            >
                {content}
            </Box>
        </Tooltip>
    );
};

const renderContractInfo = (item: VisitaApi) => {
    const organismoCentral =
        item?.establecimiento?.organismo?.organismoCentral ||
        item?.establecimiento?.organismoCentral;
    const contractName = organismoCentral?.nombre;
    const contractCuit = organismoCentral?.cuit || item?.establecimiento?.organismo?.cuit;

    if (!contractName && !contractCuit) {
        return '-';
    }

    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.25 }}>
                {contractName || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {`CUIT: ${contractCuit || '-'}`}
            </Typography>
        </Box>
    );
};

function VisitasIndex({ isHome }: { isHome?: boolean }) {
    const { isMobile, isTablet } = useIsMobile();
    const { t } = useTranslation('visitas');
    const { isAdminBO, isPreventor, isProvider, isViewerBO, isSupervisorSIMP } = useRoles();
    const routePrefix = useRoutePrefix();
    const { descargandoPdfInvestigacion, descargarPdfInvestigacion } =
        useDescargaPdfInvestigacionVisita();
    const { descargandoInformacionAdicional, descargarInformacionAdicional } =
        useDescargaInformacionAdicional();
    const access = useUserAccess();
    const FEATURES = getAccessValues(access);
    const [isShowMoreFiltersOpen, setIsShowMoreFiltersOpen] = useState<boolean>(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { handlePresentVisitClick } = useVisitActions();

    useEffect(() => {
        clearAllExecutionVisitDrafts();
    }, []);

    const {
        filters,
        setFilters,
        extraFilters,
        handleSubmitSearch,
        handleChangeExtraFilters,
        debounceSearch,
        setExtraFilters,
        clearFilters,
        handleDirectFilter
    } = useGlobalFilters({ ordenador: 'id:asc' });

    const clearFiltersRef = useRef(clearFilters);
    const handleDirectFilterRef = useRef(handleDirectFilter);

    useEffect(() => {
        clearFiltersRef.current = clearFilters;
    }, [clearFilters]);

    useEffect(() => {
        handleDirectFilterRef.current = handleDirectFilter;
    }, [handleDirectFilter]);

    const {
        selected,
        indicatorsData,
        isFetchingIndicators,
        openCard,
        anchorEl,
        menuOpen,
        handleClickAction,
        handleSelectRow,
        handleCloseMenu,
        drawerOpen,
        drawerAction,
        handleOpenDrawer,
        selectedVisita,
        setDrawerOpen,
        refetchIndicators,
        handleDownloadActa,
        handleDownloadVerosimilitud,
        handleDownloadSeguimientoVerosimilitud,
        handleDownloadVisitaTecnica,
        handleDownloadRgrl,
        currentTabIndex,
        isFetchingVisits,
        handleTabClick,
        result,
        visitSelectionHandler,
        handleCloseDrawer,
        handleExportVisitas,
        isExportingVisitas
    } = useVisitas({
        filters: filters,
        extraFilters: extraFilters,
        setFilters: setFilters,
        isHome
    });

    const {
        visitaParaElegir,
        fallasParaElegir,
        descargandoActas,
        descargarActaFallida,
        descargarActasDeFallas,
        cerrarEleccionDeFalla
    } = useActaFallida();

    const canSelectMultipleProgramVisits =
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isPreventor &&
        currentTabIndex === VISIT_TABS_INDEX.PENDING;

    const canSelectMultipleCancelVisits =
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isAdminBO &&
        currentTabIndex === VISIT_TABS_INDEX.ALL;

    const canSelectMultipleAssignVisits =
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isProvider &&
        currentTabIndex === VISIT_TABS_INDEX.REJECTED;

    const canSelectMultipleVisits =
        canSelectMultipleProgramVisits ||
        canSelectMultipleCancelVisits ||
        canSelectMultipleAssignVisits;

    // Prefijo de traducciones de la barra de selección según el flujo masivo activo.
    const getSelectionBarTranslationPrefix = (): string => {
        if (canSelectMultipleCancelVisits) return 'drawerCancelVisits';
        if (canSelectMultipleAssignVisits) return 'drawers.assignPreventor.massive';
        return 'drawerProgramVisit';
    };
    const selectionBarTranslationPrefix = getSelectionBarTranslationPrefix();

    const {
        isChecked,
        handleAllCheckPress,
        handleCheckPress,
        selectedVisitsIds,
        clearVisitsSelection
    } = visitSelectionHandler;

    const visibleVisits = useMemo(() => result?.data?.data?.data || [], [result?.data?.data?.data]);
    const selectedVisitsSummary = useMemo(() => {
        const visibleVisitsById = new Map(
            visibleVisits.map((visit: VisitaApi) => [visit.id, visit])
        );

        return selectedVisitsIds.map((id) => {
            const visit = visibleVisitsById.get(id);
            const visitData = visit as any;
            const establishmentName =
                visitData?.establecimiento?.nombre || visitData?.nombreEstablecimiento || `#${id}`;
            const visitTypes = visitData?.actividades
                ?.map(
                    (activity: any) =>
                        VISIT_TYPE_DISPLAY_NAME_OVERRIDES[activity?.tipoVisita?.id] ||
                        activity?.tipoVisita?.descripcion
                )
                .filter(Boolean)
                .join(', ');

            return {
                id,
                label: visitTypes ? `${establishmentName} - ${visitTypes}` : establishmentName,
                subLabel: visitData?.establecimiento?.codigoSRT
            };
        });
    }, [selectedVisitsIds, visibleVisits]);
    const isCancelableVisit = (visit: VisitaApi) =>
        typeof visit?.estado === 'number' && CANCELABLE_VISIT_STATES.includes(visit.estado);
    const isAssignableVisit = (visit: VisitaApi) => visit?.estado === VisitaEstado.SIN_ASIGNAR;
    const isSelectableVisit = (visit: VisitaApi) => {
        // Las acciones masivas también son de gestión (SMP-513).
        if (esDeEstablecimientoDadoDeBaja(visit)) return false;
        if (canSelectMultipleCancelVisits) return isCancelableVisit(visit);
        if (canSelectMultipleAssignVisits) return isAssignableVisit(visit);
        return true;
    };
    const selectableVisibleVisits = visibleVisits.filter(isSelectableVisit);
    const isAllSelectableChecked =
        selectableVisibleVisits.length > 0 &&
        selectableVisibleVisits.every((visit: VisitaApi) => selectedVisitsIds.includes(visit.id));

    const getColumnWidths = (): string[] => {
        const widths: string[] = [];

        if (canSelectMultipleVisits) {
            widths.push('3%');
        }

        widths.push('3%');
        widths.push('14%');
        widths.push('20%');

        if (!isPreventor) {
            widths.push('14%');
            widths.push('10%');
        }

        widths.push('12%', '9%', '9%');

        if ((isAdminBO && currentTabIndex === VISIT_TABS_INDEX.PROGRAMMED) || isPreventor) {
            widths.push('15%');
        }

        if (
            currentTabIndex !== VISIT_TABS_INDEX.CLOSED &&
            currentTabIndex !== VISIT_TABS_INDEX.IN_REVISION
        ) {
            widths.push('10%');
        }

        if (
            currentTabIndex === VISIT_TABS_INDEX.ALL ||
            currentTabIndex === VISIT_TABS_INDEX.IN_REVISION ||
            currentTabIndex === VISIT_TABS_INDEX.CLOSED
        ) {
            widths.push(currentTabIndex === VISIT_TABS_INDEX.CLOSED ? '15%' : '10%');
        }
        widths.push('6%');
        widths.push('3%');

        return widths;
    };

    const columnWidths = getColumnWidths();

    const handleFilterByEstado = (
        estadoId: number,
        tabIndex: VISIT_TABS_INDEX,
        isRoam: boolean
    ) => {
        if (isHome) {
            if (isRoam) {
                navigate('/backoffice/visitas-roam', {
                    state: { idsEstadoFiltro: estadoId, tabIndex }
                });
                return;
            }

            navigate('/backoffice/visitas', {
                state: {
                    idsEstadoFiltro: estadoId,
                    tabIndex
                }
            });
            return;
        }

        handleDirectFilter({ estado: estadoId });
    };

    useEffect(() => {
        refetchIndicators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isHome) {
            const state = location.state;

            if (state?.tabIndex !== undefined) {
                dispatch(setCurrentTab(state.tabIndex));
            }

            if (state?.idsEstadoFiltro) {
                // "estado" (singular) es la clave que los endpoints de /visitas
                // interpretan como override puntual: reemplaza el bucket de
                // estados propio de la tab por este único estado.
                handleDirectFilterRef.current({ estado: state.idsEstadoFiltro });
            }
        }
    }, [dispatch, isHome, location.state]);

    useEffect(() => {
        if (isHome) {
            dispatch(setCurrentTab(VISIT_TABS_INDEX.PENDING));
            clearFiltersRef.current();
        }
    }, [dispatch, isHome]);

    // El supervisor solo consulta visitas Presentadas: trabaja siempre en la
    // pestaña Cerradas (SMP-459).
    useEffect(() => {
        if (isSupervisorSIMP && currentTabIndex !== VISIT_TABS_INDEX.CLOSED) {
            dispatch(setCurrentTab(VISIT_TABS_INDEX.CLOSED));
        }
    }, [dispatch, isSupervisorSIMP, currentTabIndex]);

    function getLimitDateInfo(fechaLimite: string) {
        if (!fechaLimite || !dayjs(fechaLimite).isValid()) {
            return { isNearDeadline: false, tooltip: '-' };
        }

        const today = dayjs();
        const limit = dayjs(fechaLimite);

        const diff = limit.diff(today, 'day');

        const isNearDeadline = diff <= SOON_DAYS_THRESHOLD;
        const isExpired = diff < 0;

        let tooltip = '';

        if (isExpired) {
            tooltip = `Vencida hace ${Math.abs(diff)} días`;
        } else {
            tooltip = `Vence en ${diff} días`;
        }

        return { isNearDeadline, tooltip };
    }

    const headers = [
        canSelectMultipleVisits && {
            type: 'selection',
            centerColumn: true,
            template: (item: VisitaApi) => (
                <Checkbox
                    checked={isChecked(item.id)}
                    disabled={!isSelectableVisit(item)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => handleCheckPress(item)}
                />
            )
        },
        {
            type: 'data',
            onClickColumnTitleSortName: 'id',
            upperLabel: (item: VisitaApi) => item?.id || '-'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) =>
                renderOccurrenceLocationTooltip(
                    item,
                    item?.establecimiento?.nombre ? (
                        <Box>
                            <Typography>{item?.establecimiento?.nombre || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {`${t('table.SRT')}: ${item?.establecimiento?.codigoSRT || '-'}`}
                            </Typography>
                        </Box>
                    ) : (
                        '-'
                    )
                ),
            align: 'left'
        },
        {
            type: 'data',
            upperLabel: renderContractInfo,
            align: 'left'
        },
        !isPreventor && {
            type: 'data',
            upperLabel: (item: VisitaApi) => {
                if (!item?.preventor) return '-';
                const nombre = item?.preventor?.nombre?.trim();
                const apellido = item?.preventor?.apellido?.trim();
                if (!nombre && !apellido) return '-';
                return `${nombre || ''} ${apellido || ''}`.trim();
            },
            align: 'left'
        },
        !isPreventor && {
            type: 'data',
            // Sin preventor ("Sin asignar") se muestra la consultora de la visita (SMP-580)
            upperLabel: (item: VisitaApi) =>
                item?.preventor?.proveedor?.razonSocial || item?.proveedor?.razonSocial || '-',
            align: 'left'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) =>
                renderOccurrenceLocationTooltip(
                    item,
                    item?.establecimiento?.direccion?.localidad?.nombre || '-'
                ),
            align: 'left'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) =>
                renderOccurrenceLocationTooltip(
                    item,
                    item?.establecimiento?.direccion?.localidad?.provincia?.nombre || '-'
                ),
            align: 'left'
        },
        {
            type: 'data',
            onClickColumnTitleSortName: 'estado',
            upperLabel: (item: VisitaApi) =>
                item?.estado ? (
                    <VisitaEstadoChip
                        estadoId={item?.estado}
                        observacion={item?.observacion}
                        motivoFalla={item?.motivoFalla}
                    />
                ) : (
                    '-'
                )
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => {
                if (!item?.actividades || item?.actividades.length === 0) {
                    return '-';
                }
                const getTipoVisitaLabel = (tipoVisita: any) =>
                    VISIT_TYPE_DISPLAY_NAME_OVERRIDES[tipoVisita?.id] || tipoVisita?.descripcion;

                if (item?.actividades.length === 1) {
                    return getTipoVisitaLabel(item?.actividades[0]?.tipoVisita) || '-';
                }
                // Multiple activities: show "MÚLTIPLE" with tooltip of all descriptions
                const descriptions = item.actividades
                    .map((a) => getTipoVisitaLabel(a?.tipoVisita))
                    .filter(Boolean)
                    .join(', ');
                return (
                    <Tooltip title={descriptions} enterDelay={200}>
                        <span>MÚLTIPLE</span>
                    </Tooltip>
                );
            }
        },
        ((isAdminBO && currentTabIndex === VISIT_TABS_INDEX.PROGRAMMED) || isPreventor) && {
            type: 'data',
            upperLabel: (item: VisitaApi) => getDate(item?.fechaProgramada) || '-',
            align: 'left'
        },
        currentTabIndex !== VISIT_TABS_INDEX.CLOSED &&
            currentTabIndex !== VISIT_TABS_INDEX.IN_REVISION && {
                type: 'data',
                onClickColumnTitleSortName: 'fechaLimite',
                upperLabel: (item: VisitaApi) => {
                    const { isNearDeadline, tooltip } = getLimitDateInfo(item?.fechaLimite);

                    return (
                        <Tooltip title={tooltip} enterDelay={200}>
                            <span>
                                <Typography color={isNearDeadline ? 'red' : 'inherit'}>
                                    {getDate(item?.fechaLimite)}
                                </Typography>
                            </span>
                        </Tooltip>
                    );
                },
                align: 'left'
            },
        (currentTabIndex === VISIT_TABS_INDEX.ALL ||
            currentTabIndex === VISIT_TABS_INDEX.IN_REVISION ||
            currentTabIndex === VISIT_TABS_INDEX.CLOSED) && {
            type: 'data',
            upperLabel: (item: VisitaApi) => {
                const { tooltip } = getLimitDateInfo(item?.fechaVisita);

                return (
                    <Tooltip title={tooltip} enterDelay={200}>
                        <span>
                            <Typography>{getDate(item?.fechaVisita)}</Typography>
                        </span>
                    </Tooltip>
                );
            },
            align: 'left'
        },
        {
            type: 'data',
            upperLabel: (item: VisitaApi) => getYear(item?.fechaVisita) || '-'
        },
        { type: 'action', onClick: handleClickAction, compactOnMobile: true }
    ].filter(Boolean);
    // ...existing code...

    const hasActividad = (visita: any, description: 'VEROSIMILITUD' | 'RGRL'): boolean =>
        visita?.actividades?.some(
            (a: any) => a?.tipoVisita?.descripcion?.toUpperCase() === description
        ) ?? false;

    const isAllVisitsTab = currentTabIndex === VISIT_TABS_INDEX.ALL;
    const selectedVisitTabIndex = isAllVisitsTab
        ? resolveVisitTabIndexByEstado(selected?.estado)
        : currentTabIndex;

    // Visita de un establecimiento dado de baja: solo Ver visita, Ver historial y
    // descargas. Toda acción de gestión se apaga (SMP-513).
    const isGestionHabilitada = !esDeEstablecimientoDadoDeBaja(selected);

    //BO ACTIONS VALIDATIONS
    const canAssignPreventor =
        isGestionHabilitada &&
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        (isAdminBO || isProvider) &&
        selected?.estado !== undefined &&
        (isProvider
            ? [VisitaEstado.SIN_ASIGNAR].includes(selected.estado)
            : ASSIGNATION_VALID_STATES.includes(selected.estado));

    const canReassignPreventor =
        isGestionHabilitada &&
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        (isAdminBO || isProvider) &&
        selected?.estado !== undefined &&
        (isProvider
            ? [VisitaEstado.RECHAZADO, VisitaEstado.PENDIENTE, VisitaEstado.PROGRAMADA].includes(
                  selected.estado
              )
            : REASSIGNATION_VALID_STATES.includes(selected.estado));

    const canCancelVisit =
        isGestionHabilitada &&
        isAdminBO &&
        selected?.estado !== undefined &&
        selectedVisitTabIndex !== null &&
        CANCELABLE_VISIT_TABS.includes(selectedVisitTabIndex);

    //opens drawer with details
    const visitDetailsAction =
        selected?.estado !== undefined ? resolveVisitDetailsAction(selected, isAdminBO) : null;

    const canSeeDetail =
        isGestionHabilitada && (isAdminBO || isProvider) && canSeeVisitDetail(selected);

    const canSeeVisitExecution = puedeVerEjecucionVisita(selected, selectedVisitTabIndex, {
        isAdminBO,
        isViewerBO,
        isPreventor,
        isSupervisorSIMP
    });

    const canPresentVisit =
        isGestionHabilitada &&
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isAdminBO &&
        selectedVisitTabIndex === VISIT_TABS_INDEX.IN_REVISION;

    const canSendVisitToRevision =
        isGestionHabilitada &&
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isAdminBO &&
        selectedVisitTabIndex === VISIT_TABS_INDEX.IN_REVISION;

    //PREVENTOR ACTIONS VALIDATIONS

    const canProgramVisit =
        isGestionHabilitada &&
        isPreventor &&
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        selected?.estado === VisitaEstado.PENDIENTE;

    const canRegisterVisit =
        isGestionHabilitada &&
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isPreventor &&
        selectedVisitTabIndex !== VISIT_TABS_INDEX.PENDING &&
        canPreventorRegisterVisit(selected?.estado);

    // En "Todas" cada visita descarga lo mismo que en su pestaña propia: una cerrada
    // ofrece las descargas de Cerradas (SMP-513).
    const canDownloadDocsEnvelope =
        selected?.estado !== undefined &&
        selectedVisitTabIndex !== null &&
        [VISIT_TABS_INDEX.IN_REVISION, VISIT_TABS_INDEX.CLOSED].includes(selectedVisitTabIndex) &&
        (isAdminBO || isPreventor || isViewerBO || isProvider || isSupervisorSIMP);

    // Acta y Verosimilitud comparten el mismo endpoint backend y el mismo gate
    // de estado. Para PRESENTADO/REALIZADA se mantiene el criterio existente
    // (sin regresión). Para CANCELADA se habilita solo si el backend confirma
    // que la visita tiene los campos de finalización completos
    // (actaVerosimilitudDisponiblePorDatos) — no hay evidencia persistida
    // propia del acta, ver design.md de descarga-documentacion-visita-cancelada.
    const isActaEstadoPermitido = esEstadoDescargaActaPermitido(
        selected?.estado,
        selected?.actaVerosimilitudDisponiblePorDatos
    );

    const canDownloadOriginalActa = canDownloadDocsEnvelope && isActaEstadoPermitido;

    // Toda visita que falló alguna vez tiene un acta por cada falla, en
    // cualquier estado y pestaña: puede haberse retomado y hasta realizado. Una
    // visita cerrada como Fallida siempre la ofrece, aunque la grilla no traiga
    // sus fallas: se resuelven del historial al descargar (useActaFallida).
    const canDownloadActaFallida = puedeDescargarActaFallida(
        selected,
        isAdminBO || isPreventor || isViewerBO || isProvider || isSupervisorSIMP
    );

    const canRejectVisit =
        isGestionHabilitada &&
        FEATURES.GESTION_DE_VISITAS.hasAccess &&
        isPreventor &&
        selected?.estado !== undefined &&
        REJECTABLE_VISIT_STATES.includes(selected.estado);

    const canRequestChanges = selectedVisitTabIndex === VISIT_TABS_INDEX.IN_REVISION;

    const canSeeMotive =
        selected?.estado === VisitaEstado.CANCELADA &&
        selectedVisitTabIndex === VISIT_TABS_INDEX.CLOSED;

    const canDownloadVerosimilitud =
        canDownloadDocsEnvelope &&
        (selected?.estado !== undefined && ACTA_DOWNLOAD_ALLOWED_STATES.includes(selected.estado)
            ? hasActividad(selected, 'VEROSIMILITUD')
            : selected?.estado === VisitaEstado.CANCELADA &&
              Boolean(selected?.actaVerosimilitudDisponiblePorDatos) &&
              Boolean(selected?.tieneVerosimilitud));

    // Mismas reglas que el Acta y la Verosimilitud (SMP-353), más la actividad de seguimiento
    // completada.
    const canDownloadSeguimientoVerosimilitud =
        canDownloadDocsEnvelope &&
        isActaEstadoPermitido &&
        Boolean(
            selected?.actividades?.some(
                (a: any) =>
                    a?.tipoVisita?.descripcion?.toUpperCase().includes('SEGUIMIENTO VEROSIMILITUD') &&
                    a?.estado === 2
            )
        );

    const canDownloadVisitaTecnica =
        canDownloadDocsEnvelope && Boolean(selected?.tieneVisitaTecnica);

    // Informe de investigacion (SMP-401): Preventor y Admin BO, visita de investigacion aprobada.
    // También el supervisor, sobre las Presentadas de sus organismos (SMP-459).
    const canDownloadPdfInvestigacion = puedeDescargarPdfInvestigacion(
        selected,
        isPreventor || isAdminBO || isSupervisorSIMP
    );

    // En las visitas de investigacion el rgrl que llega es el del establecimiento: no se ofrece, va el
    // informe de investigacion (SMP-401).
    const canDownloadRgrl =
        canDownloadDocsEnvelope &&
        Boolean(selected?.idPresentacion || selected?.rgrl?.id) &&
        !esVisitaDeInvestigacion(selected);

    // ZIP con la "Documentación adicional" (SMP-551): pestañas con descargas, solo BackOffice y Supervisor
    // (sin Proveedor) y solo si la visita tiene adjuntos.
    const canDownloadInformacionAdicional = puedeDescargarInformacionAdicional(
        selected,
        Boolean(
            canDownloadDocsEnvelope && (isAdminBO || isViewerBO || isPreventor || isSupervisorSIMP)
        ),
        Boolean(isSupervisorSIMP)
    );

    const handleOpenVisitDetails = () => {
        if (!selected) return;

        if (isProvider) {
            navigate('/backoffice/visitas/ejecucion', {
                state: {
                    selectedVisita: selected,
                    siniestro: selected?.siniestro
                }
            });
            return;
        }

        if (selected.siniestro && selected.estado !== VisitaEstado.PENDIENTE) {
            navigate('/backoffice/visitas/ejecucion', {
                state: {
                    selectedVisita: selected,
                    siniestro: selected.siniestro
                }
            });
            return;
        }

        navigate('/backoffice/visitas/crear', {
            state: {
                mode: visitDetailsAction === 'edit' ? 'edit' : 'view',
                selectedVisita: selected,
                selectedEstablishment: selected?.establecimiento || {
                    id: selected?.idEstablecimiento,
                    nombre: selected?.nombreEstablecimiento,
                    direccion: selected?.establecimiento?.direccion
                },
                idEstablecimiento: selected?.idEstablecimiento
            }
        });
    };

    return (
        <>
            {isHome && (
                <VisitasIndicators
                    indicatorsData={indicatorsData}
                    onFilterByEstado={handleFilterByEstado}
                    isLoading={isFetchingIndicators}
                />
            )}

            {isHome && (
                <HomeAgenda
                    hoy={isFetchingIndicators ? null : indicatorsData?.hoy}
                    aVencer7={isFetchingIndicators ? null : indicatorsData?.aVencer7}
                    aVencer30={isFetchingIndicators ? null : indicatorsData?.aVencer30}
                    vencidas={isFetchingIndicators ? null : indicatorsData?.vencidas}
                    devueltas={isFetchingIndicators ? null : indicatorsData?.devueltas}
                    onProgramar={(visita) => {
                        handleOpenDrawer(DRAWER_ACTION_VALUES.PROGRAM_VISIT, visita);
                    }}
                    onRegistrar={(visita) => {
                        navigate('../backoffice/visitas/ejecucion', {
                            state: { selectedVisita: visita, siniestro: (visita as any)?.siniestro }
                        });
                    }}
                    isLoading={isFetchingIndicators}
                />
            )}

            {!isHome && (
                <>
                    <SectionHeader>
                        <SectionHeader.Title>{t('header.title')}</SectionHeader.Title>
                        <Grid display="flex" flexDirection="row" gap={1}>
                            {(isAdminBO || isProvider || isSupervisorSIMP) && (
                                <SectionHeader.IconHeader
                                    variant="outlined"
                                    text="Exportar Excel"
                                    showIcon={false}
                                    disabled={isExportingVisitas}
                                    onClick={handleExportVisitas}
                                />
                            )}
                            {FEATURES.ALTA_DE_VISITAS.hasAccess && isAdminBO && (
                                <SectionHeader.IconHeader
                                    text={t('header.add')}
                                    onClick={() => navigate('crear')}
                                />
                            )}
                        </Grid>
                    </SectionHeader>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <SearchToolbar
                                onChange={debounceSearch}
                                inputSearchName="visita"
                                label={t('filters.searchTerm')}
                                showSearchInput={false}
                                onClick={handleSubmitSearch}
                                clearFilters={clearFilters}
                                showMoreFiltersButton={true}
                                isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                                handleShowMoreFiltersPress={() =>
                                    setIsShowMoreFiltersOpen((prevState: boolean) => !prevState)
                                }
                            >
                                <VisitasFilters
                                    extraFilters={extraFilters}
                                    setExtraFilters={setExtraFilters}
                                    handleChangeExtraFilters={handleChangeExtraFilters}
                                    isShowMoreFiltersOpen={isShowMoreFiltersOpen}
                                    selectedEstablishment={
                                        location.state?.selectedEstablishment || null
                                    }
                                />
                            </SearchToolbar>
                        </Grid>
                    </Grid>
                    <Tabs
                        value={currentTabIndex}
                        onChange={handleTabClick}
                        variant="scrollable"
                        scrollButtons={isMobile ? 'auto' : false}
                        allowScrollButtonsMobile
                        sx={{
                            height: 64,
                            paddingBottom: '24px',
                            justifyContent: 'center'
                        }}
                    >
                        {(isAdminBO || isProvider) && (
                            <Tab
                                label={t('table.tabs.all')}
                                value={VISIT_TABS_INDEX.ALL}
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 2
                                }}
                            />
                        )}

                        {(isAdminBO || isProvider) && (
                            <Tab
                                label={t('table.tabs.rejected')}
                                value={VISIT_TABS_INDEX.REJECTED}
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 2
                                }}
                            />
                        )}

                        {!isSupervisorSIMP && (
                            <Tab
                                label={t('table.tabs.pending')}
                                value={VISIT_TABS_INDEX.PENDING}
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 2
                                }}
                            />
                        )}

                        {!isSupervisorSIMP && (
                            <Tab
                                label={t('table.tabs.programmed')}
                                value={VISIT_TABS_INDEX.PROGRAMMED}
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 2
                                }}
                            />
                        )}

                        {!isSupervisorSIMP && (
                            <Tab
                                label={t('table.tabs.inProgress')}
                                value={VISIT_TABS_INDEX.IN_PROGRESS}
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 2
                                }}
                            />
                        )}

                        {!isSupervisorSIMP && (
                            <Tab
                                label={t('table.tabs.inRevision')}
                                value={VISIT_TABS_INDEX.IN_REVISION}
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    px: 4,
                                    py: 2
                                }}
                            />
                        )}

                        <Tab
                            label={t('table.tabs.closed')}
                            value={VISIT_TABS_INDEX.CLOSED}
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                px: 4,
                                py: 2
                            }}
                        />
                    </Tabs>
                    {selectedVisitsIds?.length > 0 && canSelectMultipleVisits && (
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                            p={2}
                            borderRadius={1}
                            gap={2}
                        >
                            <Grid item>
                                <Typography fontWeight={700}>
                                    {t(`${selectionBarTranslationPrefix}.selectedCount`, {
                                        count: selectedVisitsIds.length
                                    })}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Stack direction="row" spacing={2}>
                                    {canSelectMultipleProgramVisits && (
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                handleOpenDrawer(
                                                    DRAWER_ACTION_VALUES.PROGRAM_VISIT,
                                                    null
                                                );
                                            }}
                                        >
                                            {t('drawerProgramVisit.buttonOpen')}
                                        </Button>
                                    )}
                                    {canSelectMultipleCancelVisits && (
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() => {
                                                handleOpenDrawer(
                                                    DRAWER_ACTION_VALUES.CANCEL_VISITS_MASSIVE,
                                                    null
                                                );
                                            }}
                                        >
                                            {t('drawerCancelVisits.buttonOpen')}
                                        </Button>
                                    )}
                                    {canSelectMultipleAssignVisits && (
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                handleOpenDrawer(
                                                    DRAWER_ACTION_VALUES.ASSIGN_PREVENTOR_MASSIVE,
                                                    null
                                                );
                                            }}
                                        >
                                            {t('drawers.assignPreventor.massive.buttonOpen')}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        onClick={clearVisitsSelection}
                                    >
                                        {t(`${selectionBarTranslationPrefix}.buttonClear`)}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    )}
                    <Grid container spacing={3}>
                        {openCard && (isMobile || isTablet) ? null : (
                            <Grid item xs={openCard ? 6 : 12} lg={openCard ? 8 : 12}>
                                {result?.data?.count === 0 && !isFetchingVisits ? (
                                    <Box
                                        my={1}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        flexDirection="column"
                                    >
                                        <Stack
                                            direction="column"
                                            justifyContent="center"
                                            alignItems="center"
                                            sx={{
                                                '& h4': {
                                                    fontSize: '18px'
                                                },
                                                '& p': {
                                                    maxWidth: '360px',
                                                    textAlign: 'center',
                                                    margin: 0,
                                                    fontSize: '16px'
                                                }
                                            }}
                                        >
                                            <img src={NoTableDataImageBO} alt="" />
                                            <h4>{t('table.noData.title')}</h4>
                                            {isHome && (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    sx={{ mt: 2 }}
                                                    onClick={() =>
                                                        navigate('../backoffice/visitas')
                                                    }
                                                >
                                                    {t('table.noData.btnRedirection')}
                                                </Button>
                                            )}
                                        </Stack>
                                    </Box>
                                ) : (
                                    <DataTable
                                        columnTitles={[
                                            ...(canSelectMultipleVisits
                                                ? [
                                                      {
                                                          label: '',
                                                          showSelectAllCheck: true,
                                                          checked: isAllSelectableChecked,
                                                          onSelectAllCheckPress: () =>
                                                              handleAllCheckPress(
                                                                  selectableVisibleVisits
                                                              )
                                                      }
                                                  ]
                                                : []),
                                            ...columnTitles(
                                                t,
                                                isPreventor,
                                                isAdminBO,
                                                currentTabIndex
                                            ).map((title) => ({ label: title }))
                                        ].filter(Boolean)}
                                        columnWidths={columnWidths}
                                        menuOpen={menuOpen}
                                        headers={headers}
                                        onSelectRow={handleSelectRow}
                                        isLoading={isFetchingVisits}
                                        items={result?.data}
                                        filters={filters}
                                        setFilters={setFilters}
                                        isFetching={isFetchingVisits}
                                    />
                                )}
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && menuOpen}
                onClose={handleCloseMenu}
            >
                {canSeeVisitExecution && (
                    <MenuItem
                        key="see-execution"
                        onClick={() => {
                            handleCloseMenu();
                            navigate(`/${routePrefix}/visitas/ejecucion`, {
                                state: {
                                    selectedVisita: selected,
                                    siniestro: (selected as any)?.siniestro
                                }
                            });
                        }}
                    >
                        {t('table.actions.seeExecution')}
                    </MenuItem>
                )}
                {FEATURES.GESTION_DE_VISITAS.hasAccess &&
                    currentTabIndex !== VISIT_TABS_INDEX.CLOSED &&
                    canCancelVisit && (
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                handleOpenDrawer(DRAWER_ACTION_VALUES.CANCEL_VISIT, selected!);
                            }}
                        >
                            {t('drawer.cancelVisit')}
                        </MenuItem>
                    )}

                {canSeeDetail && (
                    <MenuItem
                        key="see-detail"
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenVisitDetails();
                        }}
                    >
                        {t('table.actions.seeDetail')}
                    </MenuItem>
                )}

                {isAdminBO && selected && (
                    <MenuItem
                        key="see-history"
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.SEE_VISIT_HISTORY, selected!);
                        }}
                    >
                        {t('table.actions.seeHistory')}
                    </MenuItem>
                )}

                {canRegisterVisit && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            navigate('../backoffice/visitas/ejecucion', {
                                state: {
                                    selectedVisita: selected,
                                    siniestro: (selected as any)?.siniestro
                                }
                            });
                        }}
                    >
                        {t('table.actions.registerVisit')}
                    </MenuItem>
                )}

                {canProgramVisit && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.PROGRAM_VISIT, selected!);
                        }}
                    >
                        Programar visita
                    </MenuItem>
                )}
                {canRejectVisit && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.REJECT_VISIT, selected!);
                        }}
                    >
                        {t('drawer.rejectVisit')}
                    </MenuItem>
                )}
                {canAssignPreventor && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.ASSIGN_PREVENTOR, selected!);
                        }}
                    >
                        {t('table.actions.assignPreventor')}
                    </MenuItem>
                )}
                {canReassignPreventor && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.REASSIGN_PREVENTOR, selected!);
                        }}
                    >
                        {t('table.actions.reassignPreventor')}
                    </MenuItem>
                )}
                {canPresentVisit && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handlePresentVisitClick(selected!.id);
                        }}
                    >
                        {t('table.actions.presentVisit')}
                    </MenuItem>
                )}
                {canSendVisitToRevision && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(
                                DRAWER_ACTION_VALUES.SEND_VISIT_TO_REVISION,
                                selected!
                            );
                        }}
                    >
                        {t('table.actions.sendToRevision')}
                    </MenuItem>
                )}
                {canDownloadOriginalActa && (
                    <MenuItem
                        key="download-acta-original"
                        onClick={() => {
                            handleCloseMenu();
                            handleDownloadActa();
                        }}
                    >
                        {t('table.actions.downloadActa')}
                    </MenuItem>
                )}
                {canDownloadActaFallida && (
                    <MenuItem
                        key="download-acta-fallida"
                        onClick={() => {
                            handleCloseMenu();
                            descargarActaFallida(selected);
                        }}
                    >
                        {/* La palabra "fallida" solo sirve para distinguirla del acta de la
                            visita, que son documentos distintos y conviven en una visita que
                            falló y después se presentó. Si esa no se ofrece (visita cerrada
                            como Fallida), el ítem se llama como lo busca el usuario. */}
                        {t(
                            canDownloadOriginalActa
                                ? 'table.actions.downloadActaFallida'
                                : 'table.actions.downloadActa'
                        )}
                    </MenuItem>
                )}
                {canDownloadVerosimilitud && (
                    <MenuItem
                        key="download-verosimilitud"
                        onClick={() => {
                            handleCloseMenu();
                            handleDownloadVerosimilitud();
                        }}
                    >
                        {t('table.actions.downloadVerosimilitud')}
                    </MenuItem>
                )}
                {canDownloadSeguimientoVerosimilitud && (
                    <MenuItem
                        key="download-seguimiento-verosimilitud"
                        onClick={() => {
                            handleCloseMenu();
                            handleDownloadSeguimientoVerosimilitud();
                        }}
                    >
                        Descargar seguimiento verosimilitud
                    </MenuItem>
                )}
                {canDownloadVisitaTecnica && (
                    <MenuItem
                        key="download-visita-tecnica"
                        onClick={() => {
                            handleCloseMenu();
                            handleDownloadVisitaTecnica();
                        }}
                    >
                        {t('table.actions.downloadVisitaTecnica')}
                    </MenuItem>
                )}
                {canDownloadRgrl && (
                    <MenuItem
                        key="download-rgrl"
                        onClick={() => {
                            handleCloseMenu();
                            handleDownloadRgrl();
                        }}
                    >
                        {t('table.actions.downloadRgrl')}
                    </MenuItem>
                )}
                {canDownloadPdfInvestigacion && (
                    // A diferencia del resto, el menu queda abierto mientras se genera: el item muestra
                    // el spinner y no admite otro clic (SMP-401).
                    <MenuItem
                        key="download-pdf-investigacion"
                        disabled={descargandoPdfInvestigacion}
                        onClick={async () => {
                            await descargarPdfInvestigacion(selected);
                            handleCloseMenu();
                        }}
                    >
                        {t('table.actions.downloadPdfInvestigacion')}
                        {descargandoPdfInvestigacion && (
                            <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                    </MenuItem>
                )}
                {canDownloadInformacionAdicional && (
                    // Como el informe de investigacion: el menu queda abierto con el spinner mientras se
                    // arma el ZIP (SMP-551).
                    <MenuItem
                        key="download-informacion-adicional"
                        disabled={descargandoInformacionAdicional}
                        onClick={async () => {
                            await descargarInformacionAdicional(selected);
                            handleCloseMenu();
                        }}
                    >
                        {t('table.actions.downloadInformacionAdicional')}
                        {descargandoInformacionAdicional && (
                            <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                    </MenuItem>
                )}

                {/* {canRequestChanges && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.REQUEST_CHANGES, selected!);
                        }}
                    >
                        {t('table.actions.requestChanges')}
                    </MenuItem>
                )} */}
                {/* {canSeeMotive && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            handleOpenDrawer(DRAWER_ACTION_VALUES.SEE_CANCEL_MOTIVE, selected!);
                        }}
                    >
                        {t('table.actions.seeCancelMotive')}
                    </MenuItem>
                )} */}
            </Menu>

            <DescargarActasFallidaDrawer
                visita={visitaParaElegir}
                fallas={fallasParaElegir}
                descargando={descargandoActas}
                onDescargar={descargarActasDeFallas}
                onCerrar={cerrarEleccionDeFalla}
            />

            <DrawerVisitas
                open={drawerOpen}
                setOpen={setDrawerOpen}
                actionType={drawerAction}
                visita={selectedVisita}
            />
            <DrawerManager
                open={drawerOpen}
                setOpen={setDrawerOpen}
                actionType={drawerAction}
                closeDrawer={handleCloseDrawer}
                selected={selectedVisita}
                selectedVisitsIds={selectedVisitsIds}
                selectedVisitsSummary={selectedVisitsSummary}
                clearVisitsSelection={clearVisitsSelection}
                translate={t}
            />
        </>
    );
}

export default VisitasIndex;
